var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineStore } from 'pinia';
import merge from 'lodash/merge';
import { httpService, langService, reportService, toastService } from '@/core';
import { translationUrl } from '@/api/sources';
import { notificationUpdateUrl } from '@/api/notification';
import { userInfoUrl, userPulseUrl } from '@/api/user';
import { changeActiveCompanyUrl } from '@/api/company';
import { loginUrl, registerUrl } from '@/api/auth';
import { isShallowEqual } from '@/utils/data';
import { User } from '@/models/user';
import PrettyConsole from '@/utils/pretty-console';
import { usePulse } from '@/composables/pulse';
import { useTheme } from '@/composables/theme';
import { useUpdateStore } from '@/stores/update';
import { useSubscriptionStore } from '@/stores/subscription';
import { differenceInDays } from 'date-fns';
import { useForm } from '@/composables/form.ts';
import { checkFeatureFlagChanged } from '@/utils/feature-flag';
import { clearCrispSession, hideCrispChat, pushSingleCrispEvent, pushSingleCrispSessionParam, refreshCrispChat, } from '@/utils/crisp';
import { useRoute } from 'vue-router';
import { subscriptionTypesMap, twoFactorTypesMap } from '@/data/type-maps.js';
import { initGoogleAnalytics } from '@/utils/google_analytics';
import { initFacebookAnalytics } from '@/utils/facebook_analytics';
import { amplitudeService } from '@/models/amplitude-service.ts';
import { useLanguagesStore } from '@/stores/languages.ts';
export const useAuthStore = defineStore('auth', {
    state: () => ({
        isAdmin: false,
        user: null,
        notifications: [],
        company: null,
        otherCompanies: [],
        invoiceQuota: null,
        subscription_mismatch: false,
        locale: null,
        twoFactorChallengeLoading: false,
        twoFactorChallengeModalVisible: false,
        twoFactorChallengeCompleted: false,
        twoFactorToggleLoading: false,
        passwordConfirmModalVisible: false,
        passwordConfirmLoading: false,
        two_factor_challenge_email: null,
        two_factor_challenge_phone: null,
        two_factor_challenge_type: null,
        previewModes: [],
        onboardingGuideVisible: false,
        twoFactorChallengeForm: useForm({
            code: null,
            use_recovery_code: false,
        }),
        confirmPasswordForm: useForm({
            password: null,
        }),
        confirmTwoFactorForm: useForm({
            code: null,
            two_factor_type: null,
        }),
        feature_flags_changed: false,
        auth_feature_flags: [],
        experiments: {},
        userVerifyModalVisible: false,
        smartIdModalVisible: false,
        twoFactorModalType: null,
        twoFactorToptConfirmModalVisible: false,
        twoFactorEmailConfirmModalVisible: false,
        twoFactorSmsConfirmModalVisible: false,
    }),
    getters: {
        hasAnyCompany(state) {
            return (Boolean(state.company)) || state.otherCompanies.length > 0;
        },
        isLoggedIn(state) {
            return state.user !== null;
        },
        fullName(state) {
            if (!state.user) {
                return '';
            }
            return `${state.user.profile.name} ${state.user.profile.last_name}`;
        },
        expirationDays(state) {
            var _a, _b, _c;
            const expiration = (_c = (_b = (_a = state.user) === null || _a === void 0 ? void 0 : _a.permissions) === null || _b === void 0 ? void 0 : _b.user_subscription) === null || _c === void 0 ? void 0 : _c.expiration;
            if (!expiration) {
                return null;
            }
            return differenceInDays(new Date(expiration), new Date());
        },
        companyCount(state) {
            let count = state.otherCompanies.filter(it => it.owner).length;
            if (state.company && state.company.owner) {
                count++;
            }
            return count;
        },
        hasUnreadNotification(state) {
            return state.notifications.some(it => it.seen === null);
        },
        unreadNotificationsLength(state) {
            return state.notifications.filter(it => it.seen === null).length;
        },
        hasConfirmedEmail(state) {
            if (!state.user) {
                return false;
            }
            return Boolean(state.user.profile.email_confirmed);
        },
        onboardingCompleted(state) {
            if (!state.user) {
                return false;
            }
            return Boolean(state.user.profile.onboarding_completed);
        },
        trialExpired(state) {
            if (!state.user) {
                return false;
            }
            return Boolean(state.user.permissions.subscription.tag === 'free' && state.user.permissions.subscription.is_trial_user);
        },
        twoFactorEnabled(state) {
            if (!state.user) {
                return false;
            }
            return Boolean(state.user.profile.two_factor_enabled);
        },
        priceDecimalPlaces(state) {
            if (state.company) {
                return state.company.price_decimal_places;
            }
            return 4;
        },
        intermediateDecimalPlaces(state) {
            if (state.company) {
                return state.company.intermediate_decimal_places;
            }
            return 2;
        },
        showTrialBar(state) {
            var _a, _b;
            return Boolean((_b = (_a = state.user) === null || _a === void 0 ? void 0 : _a.permissions) === null || _b === void 0 ? void 0 : _b.show_trial_bar);
        },
        showTrialDiscountBar(state) {
            var _a, _b;
            return Boolean((_b = (_a = state.user) === null || _a === void 0 ? void 0 : _a.permissions) === null || _b === void 0 ? void 0 : _b.show_trial_discount_bar);
        },
        promoDiscountDaysLeft(state) {
            var _a, _b, _c, _d;
            return (_d = (_c = (_b = (_a = state.user) === null || _a === void 0 ? void 0 : _a.permissions) === null || _b === void 0 ? void 0 : _b.user_subscription) === null || _c === void 0 ? void 0 : _c.promo_days_left) !== null && _d !== void 0 ? _d : -1;
        },
        showPromoBar() {
            var _a;
            return (_a = useRoute().query) === null || _a === void 0 ? void 0 : _a.latvian_promo;
        },
        showEmployeeNoPlanBar(state) {
            var _a, _b, _c;
            return state.company &&
                !state.company.owner &&
                ((_c = (_b = (_a = state.user) === null || _a === void 0 ? void 0 : _a.permissions) === null || _b === void 0 ? void 0 : _b.subscription) === null || _c === void 0 ? void 0 : _c.tag) !== subscriptionTypesMap.business;
        },
        showAnyBar() {
            return this.showTrialBar || this.showPromoBar || this.showEmployeeNoPlanBar || this.showTrialDiscountBar;
        },
        hasPersonalizationCompleted(state) {
            if (!state.user) {
                return false;
            }
            return state.user.onboarding.personalization_completed;
        }
    },
    actions: {
        setUser(payload) {
            this.user = payload;
        },
        setCompany(payload) {
            this.company = payload;
        },
        removeCompany(payload) {
            this.otherCompanies = this.otherCompanies.filter(it => it.id !== payload);
        },
        setOtherCompanies(payload) {
            this.otherCompanies = payload;
        },
        setInvoiceQuota(payload) {
            this.invoiceQuota = payload;
        },
        updateUser(payload) {
            merge(this.user, payload);
        },
        updateActiveCompany(payload) {
            var _a;
            Object.assign((_a = this.company) !== null && _a !== void 0 ? _a : {}, payload);
        },
        setNotifications(payload) {
            this.notifications = payload;
        },
        setUserSubscription(payload) {
            if (!this.user) {
                return;
            }
            this.user.permissions.user_subscription = payload;
        },
        setSubscriptionMismatch(value) {
            this.subscription_mismatch = value;
        },
        setFeatureFlags(payload) {
            if (!this.user) {
                return;
            }
            this.user.permissions.feature_flags = payload;
        },
        setFeatureFlagsChange(value) {
            this.feature_flags_changed = value;
        },
        setAuthFeatureFlags(payload) {
            this.auth_feature_flags = payload;
        },
        markUserNotificationsSeen() {
            this.notifications.forEach(it => {
                it.seen = new Date().toISOString()
                    .substring(0, 19)
                    .replace('T', '');
            });
        },
        setUserAgreements(payload) {
            if (!this.user) {
                return;
            }
            this.user.profile.gdpr = payload;
        },
        setSaveExternalClientsNotice(payload) {
            if (!this.company) {
                return;
            }
            this.company.save_external_clients_notice = payload;
        },
        updateUserModules(payload) {
            var _a, _b;
            if (!this.user) {
                return;
            }
            const enabledModule = (_a = this.user.activeCompany) === null || _a === void 0 ? void 0 : _a.enabled_modules.filter(m => (m === null || m === void 0 ? void 0 : m.type) === payload.data.type)[0];
            if (enabledModule) {
                Object.assign(enabledModule, payload.data);
            }
            else {
                (_b = this.user.activeCompany) === null || _b === void 0 ? void 0 : _b.enabled_modules.push(payload.data);
            }
        },
        setLocale(payload) {
            this.locale = payload;
        },
        setTwoFactorEnabled(payload) {
            if (!this.user) {
                return;
            }
            this.user.profile.two_factor_enabled = payload;
        },
        setConfirmPasswordModalVisible(payload) {
            this.passwordConfirmModalVisible = payload;
        },
        setConfirmPasswordLoading(payload) {
            this.passwordConfirmLoading = payload;
        },
        setTwoFactorToggleLoading(payload) {
            this.twoFactorToggleLoading = payload;
        },
        setTwoFactorChallengeModalVisible(payload) {
            this.twoFactorChallengeModalVisible = payload;
        },
        setTwoFactorChallengeLoading(payload) {
            this.twoFactorChallengeLoading = payload;
        },
        setTwoFactorChallengeCompleted(payload) {
            this.twoFactorChallengeCompleted = payload;
        },
        setTwoFactorChallengedEmail(payload) {
            this.two_factor_challenge_email = payload;
        },
        setTwoFactorChallengedPhone(payload) {
            this.two_factor_challenge_phone = payload;
        },
        setTwoFactorChallengeType(payload) {
            this.two_factor_challenge_type = payload;
        },
        setAdmin(payload) {
            this.isAdmin = payload;
        },
        setDevelopmentPreviewMode(payload) {
            this.previewModes = payload;
        },
        setExperiments(payload) {
            this.experiments = payload;
        },
        setCompanyLanguageId(payload) {
            if (!this.company) {
                return;
            }
            this.company.language_id = payload;
        },
        setTranslationsEnabled(payload) {
            if (!this.company) {
                return;
            }
            this.company.translations_enabled = payload;
        },
        setPersonalizationCompleted(payload) {
            if (!this.user) {
                return;
            }
            this.user.onboarding.personalization_completed = payload;
        },
        setOnboardingGuideVisible(payload) {
            this.onboardingGuideVisible = payload;
        },
        setUserVerifyModalVisible(payload) {
            this.userVerifyModalVisible = payload;
        },
        setSmartIdModalVisible(payload) {
            this.smartIdModalVisible = payload;
        },
        setTwoFactorModalType(payload) {
            this.twoFactorModalType = payload;
        },
        setTwoFactorToptConfirmModalVisible(payload) {
            this.twoFactorToptConfirmModalVisible = payload;
        },
        setTwoFactorEmailConfirmModalVisible(payload) {
            this.twoFactorEmailConfirmModalVisible = payload;
        },
        setTwoFactorSmsConfirmModalVisible(payload) {
            this.twoFactorSmsConfirmModalVisible = payload;
        },
        updateCrispData(customSessionData = null, refreshChat = false) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
            if (!this.user) {
                return;
            }
            if (this.isAdmin) {
                clearCrispSession();
                hideCrispChat();
                return;
            }
            if (refreshChat && !this.isAdmin) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                // TODO type crisp.js
                refreshCrispChat((_a = this.user.profile) === null || _a === void 0 ? void 0 : _a.crisp_session_token);
            }
            window.$crisp = window.$crisp || [];
            window.$crisp.push(['set', 'user:email', this.user.profile.email]);
            window.$crisp.push(['set', 'user:nickname', `${this.user.profile.name} ${this.user.profile.last_name}`]);
            window.$crisp.push([
                'set', 'session:data', [
                    [
                        ['user_id', this.user.profile.id],
                        ['trial_used', (_b = this.user.permissions) === null || _b === void 0 ? void 0 : _b.user_subscription.is_trial_used],
                        ['trial_active', (_c = this.user.permissions) === null || _c === void 0 ? void 0 : _c.user_subscription.is_trial],
                        ['user_created_at', (_d = this.user.profile) === null || _d === void 0 ? void 0 : _d.created_at],
                        ['user_created_year', ((_e = this.user.profile) === null || _e === void 0 ? void 0 : _e.created_at) ? new Date(this.user.profile.created_at).getFullYear() : null],
                        ['email_confirmed', (_f = this.user.profile) === null || _f === void 0 ? void 0 : _f.email_confirmed],
                        ['user_is_employee', this.user.is_employee],
                        ['user_language', (_g = this.user.profile) === null || _g === void 0 ? void 0 : _g.language],
                        ['inflection_name', (_h = this.user.profile) === null || _h === void 0 ? void 0 : _h.inflection_name],
                    ],
                ],
            ]);
            if ((_j = this.user.profile) === null || _j === void 0 ? void 0 : _j.created_at) {
                window.$crisp.push([
                    'set', 'session:data', [
                        [
                            ['user_created_in_days', differenceInDays(new Date(), new Date(this.user.profile.created_at))],
                        ],
                    ],
                ]);
            }
            if (this.user.activeCompany) {
                window.$crisp.push([
                    'set', 'session:data', [
                        [
                            ['company', this.user.activeCompany.full_name],
                            ['company_code', this.user.activeCompany.code],
                            ['company_type', this.user.activeCompany.company_type_title],
                            ['company_vat_code', this.user.activeCompany.vat_enabled ? this.user.activeCompany.vat_code : ''],
                            ['company_country', (_k = this.user.activeCompany) === null || _k === void 0 ? void 0 : _k.country],
                        ],
                    ],
                ]);
                window.$crisp.push([
                    'set', 'session:data', [
                        [
                            ['subscription_type', (_l = this.user.permissions) === null || _l === void 0 ? void 0 : _l.user_subscription.tag],
                        ],
                    ],
                ]);
                if (((_m = this.user.permissions) === null || _m === void 0 ? void 0 : _m.user_subscription.months) && ((_o = this.user.permissions) === null || _o === void 0 ? void 0 : _o.user_subscription.expiration)) {
                    window.$crisp.push([
                        'set', 'session:data', [
                            [
                                ['subscription_months', this.user.permissions.user_subscription.months],
                                ['subscription_ends', this.user.permissions.user_subscription.expiration],
                            ],
                        ],
                    ]);
                }
                if (customSessionData) {
                    window.$crisp.push([
                        'set', 'session:data', [customSessionData],
                    ]);
                }
            }
        },
        updateGoogleAnalytics() {
            var _a, _b;
            if (window.gtag) {
                initGoogleAnalytics((_b = (_a = this.user) === null || _a === void 0 ? void 0 : _a.profile) === null || _b === void 0 ? void 0 : _b.id);
            }
        },
        updateFacebookAnalytics() {
            var _a, _b;
            if (window.fbq) {
                initFacebookAnalytics((_b = (_a = this.user) === null || _a === void 0 ? void 0 : _a.profile) === null || _b === void 0 ? void 0 : _b.id);
            }
        },
        pulse() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
                if (!this.user) {
                    return;
                }
                try {
                    const response = yield httpService.get(userPulseUrl());
                    this.setNotifications((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.notifications);
                    if ((_d = (_c = this.user) === null || _c === void 0 ? void 0 : _c.activeCompany) === null || _d === void 0 ? void 0 : _d.id) {
                        useUpdateStore().setActiveCompanyMismatch(((_f = (_e = response.data) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.active_company_id) !== this.user.activeCompany.id);
                    }
                    if ((_h = (_g = response.data) === null || _g === void 0 ? void 0 : _g.data) === null || _h === void 0 ? void 0 : _h.company_owner_changed) {
                        useUpdateStore().setCompanyOwnerChanged(true);
                    }
                    if (!isShallowEqual((_k = (_j = this.user) === null || _j === void 0 ? void 0 : _j.permissions) === null || _k === void 0 ? void 0 : _k.user_subscription, response.data.data.user_subscription)) {
                        this.setUserSubscription(response.data.data.user_subscription);
                        this.setSubscriptionMismatch(true);
                    }
                    const featureChanged = checkFeatureFlagChanged((_m = (_l = this.user) === null || _l === void 0 ? void 0 : _l.permissions) === null || _m === void 0 ? void 0 : _m.feature_flags, response.data.data.feature_flags);
                    if (featureChanged.changed) {
                        if (featureChanged.reload) {
                            this.setFeatureFlagsChange(true);
                        }
                        else {
                            this.setFeatureFlags(response.data.data.feature_flags);
                        }
                    }
                }
                catch (e) {
                    reportService.report(e);
                }
            });
        },
        markNotificationsSeen() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    yield httpService.patch(notificationUpdateUrl());
                    this.markUserNotificationsSeen();
                }
                catch (e) {
                    reportService.report(e);
                }
            });
        },
        boot() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d, _e, _f, _g, _h;
                try {
                    const response = yield httpService.get(userInfoUrl());
                    this.setUser(new User(response.data.data));
                    this.setCompany(response.data.data.activeCompany);
                    this.setOtherCompanies(response.data.data.otherCompanies);
                    this.setInvoiceQuota(response.data.data.invoice_quota);
                    this.setAdmin(response.data.data.is_admin);
                    useTheme().syncTheme((_a = response.data.data.profile) === null || _a === void 0 ? void 0 : _a.theme);
                    this.updateCrispData(null, true);
                    this.updateGoogleAnalytics();
                    this.updateFacebookAnalytics();
                    this.setDevelopmentPreviewMode(response.data.data.preview_modes);
                    this.setExperiments(response.data.data.experiments);
                    useLanguagesStore().setInvoiceLanguages(response.data.data.invoice_languages);
                    if (!this.isAdmin) {
                        amplitudeService.initAmplitude(); // general tracking
                        amplitudeService.initEngagementPlugin((_c = (_b = this.user) === null || _b === void 0 ? void 0 : _b.profile) === null || _c === void 0 ? void 0 : _c.language); // guides and surveys
                        if (((_f = (_e = (_d = this.user) === null || _d === void 0 ? void 0 : _d.permissions) === null || _e === void 0 ? void 0 : _e.user_subscription) === null || _f === void 0 ? void 0 : _f.is_trial_user) && !((_g = this.user) === null || _g === void 0 ? void 0 : _g.is_employee)) {
                            amplitudeService.initSessionReplayPlugin(); // sessions recording
                        }
                    }
                    usePulse();
                    return response;
                }
                catch (e) {
                    const errorWithResponse = e;
                    if (((_h = errorWithResponse.response) === null || _h === void 0 ? void 0 : _h.status) === 401) {
                        PrettyConsole.debug('auth', 'User unauthenticated.');
                    }
                    else {
                        reportService.report(e);
                    }
                    amplitudeService.resetAmplitude();
                }
                return null;
            });
        },
        clear() {
            return __awaiter(this, void 0, void 0, function* () {
                this.setUser(null);
                this.setCompany(null);
                this.setOtherCompanies([]);
                refreshCrispChat();
            });
        },
        getTranslations() {
            return __awaiter(this, void 0, void 0, function* () {
                return httpService.get(translationUrl());
            });
        },
        loadTranslations(langResponse) {
            return __awaiter(this, void 0, void 0, function* () {
                yield langService.load(langResponse.data.data.locale, langResponse.data.data.translations);
            });
        },
        login(payload) {
            return __awaiter(this, void 0, void 0, function* () {
                const response = yield httpService.post(loginUrl(), payload);
                const langResponse = yield httpService.get(translationUrl());
                yield langService.load(langResponse.data.data.locale, langResponse.data.data.translations);
                return response;
            });
        },
        register(payload) {
            return __awaiter(this, void 0, void 0, function* () {
                const response = yield httpService.post(registerUrl(), payload);
                this.setUser(new User(response.data.data));
                this.setOtherCompanies([]);
                if (response.data.data.otherCompanies.length) {
                    yield httpService.post(changeActiveCompanyUrl(response.data.data.otherCompanies[0].id));
                    this.setCompany(response.data.data.otherCompanies[0]);
                }
                else {
                    this.setCompany(null);
                }
                this.updateCrispData([
                    ['nordigen_enabled', false],
                    ['invoice_created', false],
                    ['company_bank_created', false],
                    ['company_type_selected', false],
                    ['email_confirmed', false],
                ]);
                return response;
            });
        },
        changeActiveCompany(_a) {
            return __awaiter(this, arguments, void 0, function* ({ id, request = undefined }) {
                var _b, _c;
                const changedCompanyResponse = yield httpService.post(changeActiveCompanyUrl(id), request);
                const subscriptionStore = useSubscriptionStore();
                subscriptionStore.showManualPayments((_c = (_b = changedCompanyResponse.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.manual_payments);
                subscriptionStore.form.$.company = null;
                subscriptionStore.form.$.buy_for_company_id = null;
                subscriptionStore.setLoaded(false);
                subscriptionStore.selected = null;
                const response = yield httpService.get(userInfoUrl());
                this.setUser(new User(response.data.data));
                this.setCompany(response.data.data.activeCompany);
                this.setOtherCompanies(response.data.data.otherCompanies);
                this.setInvoiceQuota(response.data.data.invoice_quota);
                this.updateCrispData();
            });
        },
        updateModules(payload) {
            return __awaiter(this, void 0, void 0, function* () {
                this.updateUserModules(payload);
                if ((payload === null || payload === void 0 ? void 0 : payload.data.type) && (payload === null || payload === void 0 ? void 0 : payload.data.owned) && (payload === null || payload === void 0 ? void 0 : payload.data.enabled)) {
                    pushSingleCrispEvent(`user:modules:installed:${payload.data.type}`);
                    if (payload.data.type === 'sip') {
                        pushSingleCrispSessionParam('nordigen_enabled', true);
                    }
                }
            });
        },
        toggleTwoFactor() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                try {
                    this.setTwoFactorToggleLoading(true);
                    if (this.twoFactorEnabled) {
                        yield httpService.delete('/user/two-factor-authentication');
                        this.setTwoFactorEnabled(false);
                    }
                }
                catch (e) {
                    const errorWithResponse = e;
                    if (((_b = (_a = errorWithResponse.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) === 'Password confirmation required.') {
                        this.setConfirmPasswordModalVisible(true);
                    }
                    else {
                        toastService.fromResponse(e);
                    }
                }
                finally {
                    this.setTwoFactorToggleLoading(false);
                }
            });
        },
        toggleTwoFactorType(type) {
            return __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                this.setTwoFactorModalType(type);
                try {
                    yield httpService.post('/user/two-factor-authentication?type=' + type);
                    if (type === twoFactorTypesMap.topt) {
                        this.setTwoFactorToptConfirmModalVisible(true);
                    }
                    else if (type === twoFactorTypesMap.email) {
                        this.setTwoFactorEmailConfirmModalVisible(true);
                    }
                    else if (type === twoFactorTypesMap.sms) {
                        this.setTwoFactorSmsConfirmModalVisible(true);
                    }
                }
                catch (e) {
                    const errorWithResponse = e;
                    if (((_b = (_a = errorWithResponse.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) === 'Password confirmation required.') {
                        this.setConfirmPasswordModalVisible(true);
                    }
                    else {
                        toastService.fromResponse(e);
                    }
                }
            });
        },
        processTwoFactorChallenge() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    this.setTwoFactorChallengeLoading(true);
                    yield httpService.post('/two-factor-challenge', {
                        data: {
                            [this.twoFactorChallengeForm.$.use_recovery_code ? 'recovery_code' : 'code']: this.twoFactorChallengeForm.$.code,
                        },
                    });
                    this.setTwoFactorChallengeModalVisible(false);
                    this.setTwoFactorChallengeLoading(false);
                    this.setTwoFactorChallengeCompleted(true);
                }
                catch (e) {
                    this.twoFactorChallengeForm.$e.updateFromError(e);
                    toastService.fromResponse(e);
                    this.setTwoFactorChallengeLoading(false);
                }
            });
        },
        confirmPassword() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    this.setConfirmPasswordLoading(true);
                    yield httpService.post('/user/confirm-password', {
                        data: {
                            password: this.confirmPasswordForm.$.password,
                        },
                    });
                    this.setConfirmPasswordModalVisible(false);
                    if (this.twoFactorModalType !== null) {
                        yield this.toggleTwoFactorType(this.twoFactorModalType);
                    }
                    else {
                        yield this.toggleTwoFactor();
                    }
                }
                catch (e) {
                    this.confirmPasswordForm.$e.updateFromError(e);
                    toastService.fromResponse(e);
                }
                finally {
                    this.setConfirmPasswordLoading(false);
                }
            });
        },
        resetTwoFactor() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    yield httpService.post('/rest/reset-two-factor');
                }
                catch (e) {
                    toastService.fromResponse(e);
                }
            });
        },
    },
});
